import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Rocket, Calendar, Users, Target } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ArmGlyph, IyafWordmark } from "@/components/brand/IyafMark";
import { RotatingRing } from "@/components/brand/Sigils";
import { arms } from "@/data/content";
import ArmsNav from "@/components/site/ArmsNav";

const venture = arms.find((a) => a.key === "venture");

const PLANNED_ICONS = [Calendar, Rocket, Users, Target];

const VENTURE_METRIC_KEYS = [
  { key: "launchYear", v: "2027" },
  { key: "firstCohort", v: "20" },
  { key: "incubation", v: "6–12 mo" },
  { key: "acceleration", v: "3–6 mo" },
];

export default function Venture() {
  const { t } = useTranslation();
  const planned = t("armPages.venture.planned", { returnObjects: true });

  return (
    <div className="relative">
      <section className="relative pt-16 pb-32 overflow-hidden">
        <RotatingRing size={620} color="rgba(224,30,132,0.18)" className="absolute -left-32 top-40" />
        <RotatingRing size={420} color="rgba(224,30,132,0.25)" duration={140} className="absolute right-20 top-20" />
        <div className="container-iyaf relative grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <ArmGlyph color={venture.color} className="w-14 h-14" />
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-iyaf-magenta/10 border border-iyaf-magenta/30 text-iyaf-magenta text-xs font-mono uppercase tracking-widest">
                <span className="w-1.5 h-1.5 rounded-full bg-iyaf-magenta animate-pulse" /> {t("armCards.launching", { year: 2027 })}
              </span>
            </div>
            <IyafWordmark variant="venture" size="hero" className="mb-6" />
            <h1 className="font-display font-black tracking-tighter leading-[0.95] max-w-3xl text-balance text-4xl sm:text-5xl">
              <span className="font-editorial italic font-normal text-iyaf-ink/85">{t("armPages.venture.heroTitle")}</span>
            </h1>
            <p className="mt-8 font-editorial text-2xl text-iyaf-ink/75 leading-snug max-w-2xl">
              {t("armPages.venture.heroBody")}
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="relative aspect-[4/5] rounded-[40px] overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1559136555-9303baea8ebd?crop=entropy&cs=srgb&fm=jpg&w=1200"
                alt="Startup pitch"
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="container-iyaf">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-black/10 rounded-3xl overflow-hidden border border-black/5">
            {VENTURE_METRIC_KEYS.map((m) => (
              <div key={m.key} className="bg-white p-6">
                <p className="font-display text-3xl font-extrabold text-iyaf-magenta">{m.v}</p>
                <p className="text-[10px] uppercase tracking-widest text-iyaf-ink/55 mt-2">{t(`armPages.venture.metrics.${m.key}`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="container-iyaf">
          <p className="eyebrow text-iyaf-magenta mb-4">{t("armPages.venture.roadmapEyebrow")}</p>
          <h2 className="h2 mb-10">{t("armPages.venture.roadmapTitle")}</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {Array.isArray(planned) && planned.map((p, i) => {
              const Icon = PLANNED_ICONS[i] || Rocket;
              return (
                <motion.div
                  key={p.title}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.5, delay: i * 0.05 }}
                  className="p-8 rounded-3xl bg-white border border-black/5"
                  data-testid={`venture-plan-${i}`}
                >
                  <div className="w-12 h-12 rounded-full bg-iyaf-magenta/10 text-iyaf-magenta flex items-center justify-center mb-5">
                    <Icon size={20} />
                  </div>
                  <p className="font-display text-2xl font-semibold">{p.title}</p>
                  <p className="mt-3 text-iyaf-ink/70">{p.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 bg-iyaf-magenta text-white relative overflow-hidden">
        <div className="absolute -right-32 -top-32 w-[420px] h-[420px] rounded-full border border-white/20 animate-spin-slow" />
        <div className="container-iyaf relative">
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight max-w-3xl text-balance">
            {t("armPages.venture.partnerTitle")}
          </h2>
          <p className="mt-6 text-white/85 max-w-2xl text-lg">{t("armPages.venture.partnerBody")}</p>
          <Link to="/contact" data-testid="venture-cta-contact" className="mt-8 inline-flex items-center gap-2 px-7 py-4 rounded-full bg-white text-iyaf-magenta font-semibold hover:bg-iyaf-paper transition">
            {t("armPages.venture.partnerCta")}
          </Link>
        </div>
      </section>

      <ArmsNav currentKey="venture" />
    </div>
  );
}
